import React, { useState, useEffect } from 'react';

export interface FilterRangeProps {
  label: string;
  min: number;
  max: number;
  value: [number | null, number | null];
  onChange: (value: [number | null, number | null]) => void;
}

/**
 * FilterRange — numeric min/max with two text inputs side by side.
 */
export default function FilterRange({
  label,
  min,
  max,
  value,
  onChange,
}: FilterRangeProps) {
  const [minVal, setMinVal] = useState(value[0]?.toString() ?? '');
  const [maxVal, setMaxVal] = useState(value[1]?.toString() ?? '');

  useEffect(() => {
    setMinVal(value[0]?.toString() ?? '');
    setMaxVal(value[1]?.toString() ?? '');
  }, [value]);

  function handleMinChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newMin = e.target.value;
    setMinVal(newMin);
    const minNum = newMin ? parseInt(newMin, 10) : null;
    onChange([
      minNum !== null && isNaN(minNum) ? null : minNum,
      value[1],
    ]);
  }

  function handleMaxChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newMax = e.target.value;
    setMaxVal(newMax);
    const maxNum = newMax ? parseInt(newMax, 10) : null;
    onChange([
      value[0],
      maxNum !== null && isNaN(maxNum) ? null : maxNum,
    ]);
  }

  return (
    <div className="min-h-[44px] flex flex-col gap-1">
      <label className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold">
        {label}
      </label>
      <div className="flex items-end gap-2">
        <input
          type="number"
          value={minVal}
          onChange={handleMinChange}
          placeholder={`Min (${min})`}
          min={min}
          max={max}
          aria-label={`${label} minimum`}
          className="flex-1 bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm focus:outline-none focus:ring-2 focus:ring-accent"
        />
        <span className="text-fg-muted">—</span>
        <input
          type="number"
          value={maxVal}
          onChange={handleMaxChange}
          placeholder={`Max (${max})`}
          min={min}
          max={max}
          aria-label={`${label} maximum`}
          className="flex-1 bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm focus:outline-none focus:ring-2 focus:ring-accent"
        />
      </div>
    </div>
  );
}
